import apiClient from "../../../services/apiClient";
import { API_ENDPOINTS } from "../../../config/apiEndpoints";
import { getUserData } from "../../../services/apiService";

function orgStaffPayload(extra = {}) {
  const user = getUserData() || {};
  return {
    OrganizationId: user.OrganizationId || user.organizationId || "",
    StaffId: user.StaffId || user.staffId || "",
    ...extra,
  };
}

function getDataset(response) {
  if (!response || typeof response !== "object") return null;
  return response.data ?? response.Data ?? null;
}

function firstTable(ds, preferred = ["Data", "data"]) {
  if (!ds || typeof ds !== "object") return [];
  for (const key of preferred) {
    if (Array.isArray(ds[key])) return ds[key];
  }
  const keys = Object.keys(ds);
  for (const key of keys) {
    if (Array.isArray(ds[key])) return ds[key];
  }
  return [];
}

function secondTable(ds) {
  if (!ds || typeof ds !== "object") return [];
  const keys = Object.keys(ds);
  const arrays = keys.filter((k) => Array.isArray(ds[k]));
  if (arrays.length >= 2) return ds[arrays[1]];
  if (Array.isArray(ds.Versions)) return ds.Versions;
  return [];
}

async function postEsource(url, body) {
  const response = await apiClient.post(url, orgStaffPayload(body));
  const success = Number(response?.success ?? response?.Success ?? 0) === 1;
  const message = response?.message || response?.Message || "";
  const ds = getDataset(response);
  return { success, message, ds, response };
}

export async function fetchTemplates() {
  const { success, message, ds } = await postEsource(
    API_ENDPOINTS.ESOURCE.GET_TEMPLATES,
    { IsActive: true },
  );
  return { success, message, rows: firstTable(ds) };
}

export async function fetchTemplateById(formTemplateId) {
  const { success, message, ds } = await postEsource(
    API_ENDPOINTS.ESOURCE.GET_TEMPLATE_BY_ID,
    { FormTemplateId: formTemplateId },
  );
  return {
    success,
    message,
    template: firstTable(ds)[0] || null,
    versions: secondTable(ds),
  };
}

export async function saveTemplate(payload) {
  const result = await postEsource(API_ENDPOINTS.ESOURCE.SAVE_TEMPLATE, payload);
  const row = firstTable(result.ds)[0] || {};
  return {
    ...result,
    formTemplateId: row.FormTemplateId || row.formTemplateId || "",
  };
}

export async function publishTemplate(formTemplateId, { Reason } = {}) {
  return postEsource(API_ENDPOINTS.ESOURCE.PUBLISH_TEMPLATE, {
    FormTemplateId: formTemplateId,
    Reason: Reason || "",
  });
}

export async function createDraftVersion(formTemplateId) {
  const result = await postEsource(
    API_ENDPOINTS.ESOURCE.CREATE_DRAFT_VERSION,
    { FormTemplateId: formTemplateId },
  );
  const row = firstTable(result.ds)[0] || {};
  return {
    ...result,
    formTemplateId: row.FormTemplateId || formTemplateId || "",
    draftVersionId: row.DraftVersionId || row.draftVersionId || "",
    draftVersionNo: row.DraftVersionNo ?? row.draftVersionNo ?? null,
  };
}

export async function fetchInstances(filters = {}) {
  const { success, message, ds } = await postEsource(
    API_ENDPOINTS.ESOURCE.GET_INSTANCES,
    filters,
  );
  return { success, message, rows: firstTable(ds) };
}

export async function createInstance(payload) {
  const result = await postEsource(
    API_ENDPOINTS.ESOURCE.CREATE_INSTANCE,
    payload,
  );
  const row = firstTable(result.ds)[0] || {};
  return {
    ...result,
    formInstanceId: row.FormInstanceId || row.formInstanceId || "",
  };
}

export async function fetchInstanceById(formInstanceId) {
  const { success, message, ds } = await postEsource(
    API_ENDPOINTS.ESOURCE.GET_INSTANCE_BY_ID,
    { FormInstanceId: formInstanceId },
  );
  return { success, message, instance: firstTable(ds)[0] || null };
}

export async function saveDraftResponse(payload) {
  return postEsource(API_ENDPOINTS.ESOURCE.SAVE_DRAFT, payload);
}

export async function submitForReview(payload) {
  return postEsource(API_ENDPOINTS.ESOURCE.SUBMIT_REVIEW, payload);
}

export async function signInstance(payload) {
  return postEsource(API_ENDPOINTS.ESOURCE.SIGN, payload);
}

export async function lockInstance(formInstanceId) {
  return postEsource(API_ENDPOINTS.ESOURCE.LOCK, {
    FormInstanceId: formInstanceId,
  });
}

export async function amendField(payload) {
  return postEsource(API_ENDPOINTS.ESOURCE.AMEND, payload);
}

export async function fetchAuditLog(filters = {}) {
  const { success, message, ds } = await postEsource(
    API_ENDPOINTS.ESOURCE.AUDIT_LOG,
    filters,
  );
  return { success, message, rows: firstTable(ds) };
}

export async function createFillLink(payload) {
  const result = await postEsource(
    API_ENDPOINTS.ESOURCE.CREATE_FILL_LINK,
    payload,
  );
  const row = firstTable(result.ds)[0] || {};
  return {
    ...result,
    fillUrl: row.FillUrl || row.fillUrl || "",
    accessTokenId: row.AccessTokenId || row.accessTokenId || "",
    expiresAt: row.ExpiresAt || row.expiresAt || null,
  };
}

export async function revokeFillLink(payload) {
  return postEsource(API_ENDPOINTS.ESOURCE.REVOKE_FILL_LINK, payload);
}

export {
  parseSchemaJson,
  createEmptyField as emptyField,
} from "./esourceFieldTypes";

export function parseResponseJson(raw) {
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw || "{}") : raw || {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}
